import { createContext, useContext, useLayoutEffect, useRef } from 'react';

/**
 * Header search of a tournament list tab: suggestions and the `q` it writes
 * belong to the tab's list (`useTournamentSearchScope`), so only those tabs
 * opt in.
 */
export interface HeaderSearchConfig {
  /** Already translated. */
  placeholder: string;
}

export interface PageConfig {
  title: string;
  showHeader: boolean;
  showBottomNav: boolean;
  showNavigation: boolean | undefined;
  hiddenNavPaths: string[];
  className: string;
  fluid: boolean;
  headerSolid: boolean;
  /** Search icon in the header (opt-in per page); null — no header search. */
  headerSearch: HeaderSearchConfig | null;
  hideLanguageSwitcher: boolean;
}

export const DEFAULT_PAGE_CONFIG: PageConfig = {
  title: 'PClash',
  showHeader: true,
  showBottomNav: true,
  showNavigation: undefined,
  hiddenNavPaths: [],
  className: '',
  fluid: false,
  headerSolid: false,
  headerSearch: null,
  hideLanguageSwitcher: false,
};

interface PageConfigContextType {
  config: PageConfig;
  setConfig: (update: Partial<PageConfig>) => void;
  resetConfig: () => void;
}

export const PageConfigContext = createContext<PageConfigContextType | undefined>(undefined);

/**
 * Raw context hook for internal layout components (AppLayout).
 * Pages should use usePageConfig() instead.
 */
export function usePageConfigContext() {
  const context = useContext(PageConfigContext);
  if (context === undefined) {
    throw new Error('usePageConfigContext must be used within a PageConfigProvider');
  }
  return context;
}

/**
 * Hook for pages to configure the AppLayout shell.
 *
 * Declarative usage (static config):
 *   usePageConfig({ title: 'Admin', headerSolid: true });
 *
 * Imperative usage (dynamic config):
 *   const { setConfig } = usePageConfig();
 *   useEffect(() => { setConfig({ title: data.name }); }, [data]);
 */
export function usePageConfig(initialConfig?: Partial<PageConfig>) {
  const context = useContext(PageConfigContext);
  if (context === undefined) {
    throw new Error('usePageConfig must be used within a PageConfigProvider');
  }

  const { setConfig, resetConfig } = context;

  // Apply config synchronously on every mount and when its props change
  const serializedRef = useRef('');
  if (initialConfig) {
    serializedRef.current = JSON.stringify(initialConfig);
  }

  useLayoutEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedRef.current, setConfig]);

  // Reset to defaults when this component (layout) unmounts.
  // MUST be useLayoutEffect (not useEffect) so the cleanup runs synchronously before paint:
  // when navigating between routes, the old layout's cleanup and the new layout's effect must
  // both fire in one sync batch BEFORE the browser repaints. With useEffect, cleanup runs after
  // paint and overwrites the new layout's freshly-applied config (e.g. /tournaments → /dashboard
  // briefly resurrects the route-derived nav swiper that HomePage already disabled).
  useLayoutEffect(() => {
    return () => { resetConfig(); };
  }, [resetConfig]);

  return { setConfig, config: context.config };
}
